/**
 * IACalculator Component
 * Main component for the Improvement Activities calculator
 * Split-pane layout with attestation cards on left and score summary on right
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Users, User, Plus, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { IAModeledBanner } from './IAModeledBanner';
import { IARequirementsBanner } from './IARequirementsBanner';
import { IAScoreSummary } from './IAScoreSummary';
import { IAAttestationCard } from './IAAttestationCard';
import { IAActivitySelectorModal } from './IAActivitySelectorModal';
import {
  getIARequirements,
  getIAAttestations,
  getIAScore,
  saveIAAttestation,
  addIASelection,
  removeIASelection,
  resetAllIA,
  type IAAttestation,
  type IARequirements,
  type IAScore,
  type IAAttestationInput,
} from '@/lib/ia-calculator-utils';
import { type Provider } from '@/lib/measure-outcomes';
import { useProgramYear } from '@/contexts/ProgramYearContext';

interface IACalculatorProps {
  practiceId: string;
  practiceName?: string;
  useModeledIa: boolean;
  modeledIaPercent: number;
  iaWeight: number;
  onToggleModeled: (useModeled: boolean) => void;
  /** Callback with raw points score (0-40). Parent applies weights. */
  onScoreChange?: (rawScore: number) => void;
  providers?: Provider[];
  evaluationType: 'GROUP' | 'NPI';
  selectedNpi?: string;
  onEvaluationTypeChange?: (type: 'GROUP' | 'NPI') => void;
  onNpiChange?: (npi: string) => void;
  /** When true (e.g. locked year), all inputs are read-only and mutations are disabled. */
  disabled?: boolean;
}

export function IACalculator({
  practiceId,
  practiceName,
  useModeledIa,
  modeledIaPercent,
  iaWeight,
  onToggleModeled,
  onScoreChange,
  providers: externalProviders,
  evaluationType,
  selectedNpi,
  onEvaluationTypeChange,
  onNpiChange,
  disabled = false,
}: IACalculatorProps) {
  const { programYear } = useProgramYear();
  // State
  const [attestations, setAttestations] = useState<IAAttestation[]>([]);
  const [requirements, setRequirements] = useState<IARequirements | null>(null);
  const [score, setScore] = useState<IAScore | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [providers, setProviders] = useState<Provider[]>(externalProviders || []);
  const [selectorModalOpen, setSelectorModalOpen] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  
  // Refs for callbacks
  const onScoreChangeRef = useRef(onScoreChange);
  useEffect(() => {
    onScoreChangeRef.current = onScoreChange;
  }, [onScoreChange]);

  // Update providers when external providers change
  useEffect(() => {
    if (externalProviders) {
      setProviders(externalProviders);
    }
  }, [externalProviders]);

  // Track initial load vs. subsequent updates
  const hasLoadedRef = useRef(false);

  // Load data when practice/NPI changes
  useEffect(() => {
    const loadData = async () => {
      // Only show full skeleton on initial load, not when switching between modes
      if (!hasLoadedRef.current) {
        setIsLoading(true);
      }
      try {
        const npi = evaluationType === 'NPI' ? selectedNpi : undefined;
        
        // Fetch requirements, attestations, and score in parallel
        const [reqData, attData, scoreData] = await Promise.all([
          getIARequirements(practiceId, { year: programYear }),
          getIAAttestations(practiceId, { npi, year: programYear }),
          getIAScore(practiceId, { npi, year: programYear }),
        ]);
        
        setRequirements(reqData);
        setAttestations(attData);
        setScore(scoreData);
        
        // Notify parent of raw score - parent applies weights
        if (!useModeledIa && onScoreChangeRef.current && scoreData) {
          onScoreChangeRef.current(scoreData.totalScore);
        }
      } catch (error) {
        console.error('Error loading IA data:', error);
        setSaveError('Failed to load IA data. Please refresh the page.');
      } finally {
        setIsLoading(false);
        hasLoadedRef.current = true;
      }
    };

    loadData();
  }, [practiceId, evaluationType, selectedNpi, useModeledIa, programYear]);

  // Handle modeled toggle
  const handleToggleModeled = async (useModeled: boolean) => {
    onToggleModeled(useModeled);
  };

  // Handle attestation update
  const handleAttestationChange = useCallback(async (
    activityId: string,
    updates: IAAttestationInput
  ) => {
    if (disabled) return; // locked year -- no writes
    setIsSaving(true);
    setSaveError(null);
    try {
      const npi = evaluationType === 'NPI' ? selectedNpi : undefined;
      // The year MUST go in the options arg (query string) -- the backend
      // resolves the year from the query, not the body.
      const updatedAttestation = await saveIAAttestation(
        practiceId,
        activityId,
        { ...updates, npi },
        { year: programYear },
      );
      
      // Update local state
      setAttestations(prev => 
        prev.map(a => a.ia_activity_id === activityId ? updatedAttestation : a)
      );
      
      // Refresh score
      const newScore = await getIAScore(practiceId, { npi, year: programYear });
      setScore(newScore);
      
      // Notify parent of raw score change - parent applies weights
      if (!useModeledIa && onScoreChangeRef.current && newScore) {
        onScoreChangeRef.current(newScore.totalScore);
      }
      
      // Set last saved time
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving attestation:', error);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [practiceId, evaluationType, selectedNpi, useModeledIa, programYear, disabled]);

  // Handle adding an activity
  const handleAddActivity = async (activityId: string) => {
    if (disabled) return;
    const npi = evaluationType === 'NPI' ? selectedNpi : undefined;
    const result = await addIASelection(practiceId, activityId, { npi, year: programYear });
    
    if (result) {
      // Refresh attestations
      const attData = await getIAAttestations(practiceId, { npi, year: programYear });
      setAttestations(attData);
      
      // Expand the newly added card
      setExpandedCards(prev => new Set([...prev, activityId]));
    }
    
    setSelectorModalOpen(false);
  };

  // Handle removing an activity
  const handleRemoveActivity = async (activityId: string) => {
    if (disabled) return;
    const npi = evaluationType === 'NPI' ? selectedNpi : undefined;
    const success = await removeIASelection(practiceId, activityId, { npi, year: programYear });
    
    if (success) {
      setAttestations(prev => prev.filter(a => a.ia_activity_id !== activityId));
      setExpandedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(activityId);
        return newSet;
      });
      
      // Refresh score
      const newScore = await getIAScore(practiceId, { npi, year: programYear });
      setScore(newScore);
      
      // Notify parent of raw score change - parent applies weights
      if (!useModeledIa && onScoreChangeRef.current && newScore) {
        onScoreChangeRef.current(newScore.totalScore);
      }
    }
  };

  // Handle card expand/collapse
  const handleToggleExpand = (activityId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(activityId)) {
        newSet.delete(activityId);
      } else {
        newSet.add(activityId);
      }
      return newSet;
    });
  };

  // State for reset operation
  const [isResetting, setIsResetting] = useState(false);

  // Handle reset all
  const handleReset = useCallback(async () => {
    if (disabled) return;
    if (window.confirm('Are you sure you want to reset all IA attestations? This action cannot be undone.')) {
      setIsResetting(true);
      setSaveError(null);
      
      try {
        const npi = evaluationType === 'NPI' ? selectedNpi : undefined;
        
        // Call the reset all endpoint
        const result = await resetAllIA(practiceId, { npi, year: programYear });
        
        if (result.success) {
          // Clear local state
          setAttestations([]);
          setExpandedCards(new Set());
          setScore(null);
          
          // Refetch data to ensure UI is in sync with server
          const [attData, scoreData] = await Promise.all([
            getIAAttestations(practiceId, { npi, year: programYear }),
            getIAScore(practiceId, { npi, year: programYear }),
          ]);
          
          setAttestations(attData);
          setScore(scoreData);
          
          // Notify parent of raw score change - parent applies weights
          if (!useModeledIa && onScoreChangeRef.current) {
            onScoreChangeRef.current(scoreData?.totalScore || 0);
          }
        } else {
          setSaveError('Failed to reset IA data. Please try again.');
        }
      } catch (error) {
        console.error('Error resetting IA data:', error);
        setSaveError('Failed to reset IA data. Please try again.');
      } finally {
        setIsResetting(false);
      }
    }
  }, [practiceId, evaluationType, selectedNpi, useModeledIa, programYear, disabled]);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const completedCount = score?.completedCount || 0;
  const requiredCount = requirements?.activitiesRequired || 2;

  return (
    <div className="space-y-4">
      {/* Modeled Score Banner */}
      <IAModeledBanner
        useModeledIa={useModeledIa}
        modeledIaPercent={modeledIaPercent}
        onToggleModeled={handleToggleModeled}
        isUpdating={isSaving}
        disabled={disabled}
      />

      {/* Evaluation Type Toggle & NPI Selection (for NPI mode) */}
      {evaluationType === 'NPI' && (
        <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant={evaluationType === 'GROUP' ? 'default' : 'outline'}
                size="sm"
                onClick={() => onEvaluationTypeChange?.('GROUP')}
              >
                <Users className="w-4 h-4 mr-2" />
                Group/TIN
              </Button>
              <Button
                variant={evaluationType === 'NPI' ? 'default' : 'outline'}
                size="sm"
                onClick={() => onEvaluationTypeChange?.('NPI')}
              >
                <User className="w-4 h-4 mr-2" />
                NPI
              </Button>
            </div>

            {evaluationType === 'NPI' && (
              <Select
                value={selectedNpi || ''}
                onValueChange={(v) => onNpiChange?.(v)}
              >
                <SelectTrigger className="w-[280px]">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.npi} value={p.npi}>
                      {p.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      )}

      {/* When modeled, show only the disabled message */}
      {useModeledIa ? (
        <Alert className="bg-muted/50">
          <AlertDescription>
            The IA Calculator is disabled while using modeled scores. 
            Switch to the calculator to enter actual IA attestation data.
          </AlertDescription>
        </Alert>
      ) : (
        /* Main Content - Split Pane Layout (only when calculator is active) */
        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
          {/* Left Pane - Attestation Cards (60% on large screens) */}
          <div className="flex-1 lg:flex-[3] space-y-4">
            {/* Save Status */}
            <div className="flex items-center justify-end gap-2 text-sm">
              {isSaving ? (
                <>
                  <RefreshCw className="w-3 h-3 animate-spin text-muted-foreground" />
                  <span className="text-muted-foreground">Saving...</span>
                </>
              ) : saveError ? (
                <>
                  <AlertTriangle className="w-3 h-3 text-destructive" />
                  <span className="text-destructive">{saveError}</span>
                </>
              ) : lastSaved ? (
                <>
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                  <span className="text-muted-foreground">Saved {lastSaved.toLocaleTimeString()}</span>
                </>
              ) : null}
            </div>

            {/* Requirements Banner */}
            <IARequirementsBanner
              requirements={requirements}
              completedCount={completedCount}
              totalSelected={attestations.length}
            />

            {/* Attestation Cards */}
            <div className="space-y-3">
              {attestations.length === 0 ? (
                <div className="text-center py-12 bg-muted/30 rounded-lg border border-dashed">
                  <p className="text-muted-foreground mb-4">
                    No improvement activities selected yet.
                  </p>
                  <Button onClick={() => setSelectorModalOpen(true)} disabled={disabled}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Activity
                  </Button>
                </div>
              ) : (
                <>
                  {attestations.map((attestation) => (
                    <IAAttestationCard
                      key={attestation.ia_activity_id}
                      attestation={attestation}
                      practiceId={practiceId}
                      isExpanded={expandedCards.has(attestation.ia_activity_id)}
                      onToggleExpand={() => handleToggleExpand(attestation.ia_activity_id)}
                      onChange={(updates) => handleAttestationChange(attestation.ia_activity_id, updates)}
                      onRemove={() => handleRemoveActivity(attestation.ia_activity_id)}
                      isGroupLevel={evaluationType === 'GROUP'}
                      providers={providers}
                      disabled={useModeledIa || disabled}
                      isSaving={isSaving}
                    />
                  ))}
                  
                  {/* Add Activity Button */}
                  <Button
                    variant="outline"
                    className="w-full border-dashed"
                    onClick={() => setSelectorModalOpen(true)}
                    disabled={disabled}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Activity
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Right Pane - Score Panel (40% on large screens) */}
          <div className="lg:flex-[2] lg:min-w-[320px] lg:max-w-[480px]">
            {/* Spacer to align with left pane: matches save status height + space-y-4 gap */}
            <div className={(isSaving || saveError || lastSaved) ? "h-9" : "h-4"} />
            <IAScoreSummary
              score={score}
              requirements={requirements}
              iaWeight={iaWeight}
              attestations={attestations}
              onReset={handleReset}
              onAddActivity={() => setSelectorModalOpen(true)}
              isResetting={isResetting}
              disabled={disabled}
            />
          </div>
        </div>
      )}

      {/* Activity Selector Modal */}
      <IAActivitySelectorModal
        open={selectorModalOpen}
        onOpenChange={setSelectorModalOpen}
        practiceId={practiceId}
        selectedActivityIds={attestations.map(a => a.ia_activity_id)}
        onSelectActivity={handleAddActivity}
      />
    </div>
  );
}

